// Conversão especial para valores null (para MySQL)
// Conversão especial para valores null (para MySQL)
import {
  CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem, Checkbox,
  ListItemText, OutlinedInput, Box, Typography, FormGroup, FormControlLabel,
  Radio, RadioGroup, Chip, useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { normalizarId } from "../hooks/useScope";
import { useTranslation } from "react-i18next";
import api from "../services/api";

interface Access {
  userId: number;
  accessUser: number;
  accessTenant: number;
  accessStable: number | null;
  accessStall: number | null;
  accessHorse: number | null;
  accessRole: number | null;
  accessAlert: number;
  horseName?: string;
  roleName?: string;
  stableName?: string;
  stallName?: string;
  tenantNickName?: string;
}

interface AccessModalProps {
  open: boolean;
  onClose: () => void;
  userId: number | undefined;
}

type AccessType = 'full' | 'stable' | 'stall' | 'horse';

export default function AccessModal({ open, onClose, userId }: AccessModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [tenants, setTenants] = useState([]);
  const [stables, setStables] = useState([]);
  const [stalls, setStalls] = useState([]);
  const [horses, setHorses] = useState([]);

  const [selectedTenants, setSelectedTenants] = useState<number[]>([]);
  const [selectedStables, setSelectedStables] = useState<number[]>([]);
  const [selectedStalls, setSelectedStalls] = useState<number[]>([]);
  const [selectedHorses, setSelectedHorses] = useState<number[]>([]);

  // Estado para acessos atuais do usuário
  const [currentAccess, setCurrentAccess] = useState<Access[]>([]);
  const [loadingAccess, setLoadingAccess] = useState(false);

  // Estados para feedback
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Tipo de acesso selecionado
  const [accessType, setAccessType] = useState<AccessType>('full');
  
  // Estado para controlar o nível de alerta (0 a 3)
  const [accessAlert, setAccessAlert] = useState<number>(0);

  // Estado para tenant associado ao usuário
  const [userTenant, setUserTenant] = useState<number | null>(null);
  // Parceiro do usuário que está recebendo o acesso. Recorta a lista de
  // proprietários: quem é de um parceiro não pode receber acesso a
  // proprietário de outro.
  const [userPartner, setUserPartner] = useState<number | null>(null);
  const [userTenantName, setUserTenantName] = useState<string>('');
  const [loadingUserData, setLoadingUserData] = useState(false);
  /// Gravação em curso. Sem isto o botão aceita um segundo clique, tudo é
  /// enviado de novo, e a segunda leva volta como ERRO — porque o acesso que
  /// ela tenta criar já foi criado pela primeira. O usuário vê "erro" num
  /// salvamento que na verdade deu certo.
  const [salvando, setSalvando] = useState(false);

  // Estados para controlar abertura dos selects
  const [openTenantSelect, setOpenTenantSelect] = useState(false);
  const [openStableSelect, setOpenStableSelect] = useState(false);
  const [openStallSelect, setOpenStallSelect] = useState(false);

  // Opções de alerta
  const alertOptions = [
    { value: 0, label: t("access.alertNone") },
    { value: 1, label: t("access.alertLevelN", { n: 1 }) },
    { value: 2, label: t("access.alertLevelN", { n: 2 }) },
    { value: 3, label: t("access.alertLevelN", { n: 3 }) }
  ];

  // Limpar seleções quando o tipo de acesso muda
  useEffect(() => {
    setSelectedStables([]);
    setSelectedStalls([]);
    setSelectedHorses([]);
  }, [accessType]);

  // Função para carregar dados do usuário e verificar tenant associado
  const loadUserData = async () => {
    if (!userId) return;
    
    setLoadingUserData(true);
    try {
      // Buscar dados do usuário
      const response = await api.get(`/user/${userId}`);
      const userData = response.data.user || response.data;
      
      setUserPartner(normalizarId(userData.userPartner));

      // ZERO é um proprietário de verdade. Valor-verdade o trataria como
      // "sem proprietário" e mostraria a seleção livre para quem já está
      // preso a um.
      const tenantDoUsuario = normalizarId(userData.userTenant);
      if (tenantDoUsuario !== null) {
        setUserTenant(tenantDoUsuario);
        
        // Buscar nome do tenant
        const tenantResponse = await api.get(`/tenant/${tenantDoUsuario}`);
        const tenantData = tenantResponse.data.tenant || tenantResponse.data;
        setUserTenantName(tenantData.tenantName || tenantData.tenantNickName || 'Tenant');
        
        // Definir automaticamente o tenant selecionado
        setSelectedTenants([tenantDoUsuario]);
      } else {
        setUserTenant(null);
        setUserTenantName('');
      }
    } catch (err) {
      console.error("Erro ao carregar dados do usuário:", err);
      setUserTenant(null);
      setUserTenantName('');
      setUserPartner(null);
    } finally {
      setLoadingUserData(false);
    }
  };

  useEffect(() => {
    if (open && userId) {
      // Carregar dados do usuário primeiro
      loadUserData();
      
      // Carregar tenants (apenas se não houver tenant associado)
      api.get("/tenant")
        .then(res => setTenants(res.data.tenants || res.data))
        .catch(err => console.error("Erro ao carregar tenants:", err));

      // Carregar acessos atuais do usuário
      loadCurrentAccess();
    }
  }, [open, userId]);

  // Proprietários que este usuário PODE receber. Mesma regra do cadastro:
  // segue o parceiro dele, e "sem parceiro" não recorta.
  //
  // DEGRADAÇÃO: se nenhum proprietário traz `tenantPartnerId`, não há o que
  // recortar e a lista passa inteira — recortar aqui deixaria a seleção vazia
  // e ninguém conseguiria conceder acesso.
  const tenantsDisponiveis = useMemo(() => {
    if (userPartner === null) return tenants;
    const algumTemParceiro = (tenants as any[]).some(
      (tnt) => normalizarId(tnt?.tenantPartnerId) !== null,
    );
    if (!algumTemParceiro) return tenants;
    return (tenants as any[]).filter(
      (tnt) => normalizarId(tnt?.tenantPartnerId) === userPartner,
    ) as never[];
  }, [tenants, userPartner]);

  // Função para carregar os acessos atuais do usuário
  const loadCurrentAccess = async () => {
    if (!userId) return;
    
    setLoadingAccess(true);
    try {
      const response = await api.get(`/get_access/${userId}`);
      const accessData = response.data.access || response.data || [];
      setCurrentAccess(Array.isArray(accessData) ? accessData : [accessData]);
    } catch (err) {
      console.error("Erro ao carregar acessos atuais:", err);
      setCurrentAccess([]);
    } finally {
      setLoadingAccess(false);
    }
  };

  // Quando um tenant é selecionado, carrega dados relacionados
  useEffect(() => {
    if (selectedTenants.length > 0) {
      const tenantId = selectedTenants[0];
      
      // Carregar estábulos do tenant selecionado
      api.get(`/stable/${tenantId}`)
        .then(res => setStables(res.data.stables || res.data))
        .catch(err => {
          console.error("Erro ao buscar estábulos:", err);
          setStables([]);
        });
      
      // Carregar cavalos do tenant selecionado
      api.get(`/horse/${tenantId}`)
        .then(res => setHorses(res.data.horses || res.data))
        .catch(err => {
          console.error("Erro ao buscar cavalos:", err);
          setHorses([]);
        });
    } else {
      // Limpar dados se nenhum tenant estiver selecionado
      setStables([]);
      setSelectedStables([]);
      setHorses([]);
      setSelectedHorses([]);
      setStalls([]);
      setSelectedStalls([]);
    }
  }, [selectedTenants]);

  // Trocar para acesso por baia com vários proprietários escolhidos: fica só o
  // primeiro, senão as baias dele seriam gravadas para todos os outros.
  useEffect(() => {
    if (accessType === 'stall' && selectedTenants.length > 1) {
      setSelectedTenants([selectedTenants[0]]);
    }
  }, [accessType, selectedTenants]);

  // Quando estábulos são selecionados, carrega baias se o tipo for 'stall'
  useEffect(() => {
    if (selectedTenants.length > 0 && accessType === 'stall') {
      const tenantId = selectedTenants[0];
      
      // Carregar todas as baias do tenant
      api.get(`/stall/${tenantId}`)
        .then(res => {
          const allStalls = res.data.stalls || res.data;
          setStalls(allStalls);
        })
        .catch(err => {
          console.error("Erro ao buscar baias:", err);
          setStalls([]);
        });
    } else {
      setStalls([]);
      setSelectedStalls([]);
    }
  }, [selectedTenants, accessType]);

  // Função para verificar se existe acesso total ao tenant (apenas para informação)
  const hasFullTenantAccess = (tenantId: number) => {
    return currentAccess.some(access => 
      access.accessTenant === tenantId && 
      !access.accessStable && 
      !access.accessStall && 
      !access.accessHorse
    );
  };

  // Função para remover um acesso específico - CORRIGIDA
  const removeAccess = async (access: Access) => {
    if (!window.confirm(t('access.confirmRemove'))) {
      return;
    }

    try {
      const payload: any = {
        accessTenant: access.accessTenant
      };

      // Verificar se é acesso individualizado e adicionar o campo específico
      if (access.accessStable) {
        payload.accessStable = access.accessStable;
      } else if (access.accessStall) {
        payload.accessStall = access.accessStall;
      } else if (access.accessHorse) {
        payload.accessHorse = access.accessHorse;
      }

      await api.delete(`/delete_access/${userId}`, {
        data: payload,
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': access.accessTenant.toString()
        }
      });

      console.log("Acesso removido com sucesso");
      setSuccessMessage(t('access.removeSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
      loadCurrentAccess(); // Recarregar a lista
    } catch (err) {
      console.error("Erro ao remover acesso:", err);
      alert(t("access.removeError"));
    }
  };

  // Função para interpretar o tipo de acesso baseado nos dados
  const interpretAccessType = (access: Access) => {
    // Se tem cavalo específico
    if (access.accessHorse && access.accessHorse !== 0) {
      return 'horse';
    }
    // Se tem baia específica
    if (access.accessStall && access.accessStall !== 0) {
      return 'stall';
    }
    // Se tem estábulo específico
    if (access.accessStable && access.accessStable !== 0) {
      return 'stable';
    }
    // Se só tem tenant (acesso total)
    return 'full';
  };

  // Função para formatar a descrição do acesso
  const formatAccessDescription = (access: Access) => {
    const type = interpretAccessType(access);
    const alertLevel = alertOptions.find(opt => opt.value === access.accessAlert)?.label || t('access.notDefined');
    
    switch (type) {
      case 'full':
        return {
          typeKey: 'full',
          type: t('access.typeTotal'),
          details: t('access.detailsTotal'),
          items: `Tenant: ${access.tenantNickName || 'N/A'}`,
          alert: alertLevel
        };
      
      case 'stable':
        return {
          typeKey: 'stable',
          type: t('access.typeStable'),
          details: t('access.detailsStable'),
          items: `Estábulo: ${access.stableName || 'N/A'}`,
          alert: alertLevel
        };
      
      case 'stall':
        return {
          typeKey: 'stall',
          type: t('access.typeStall'),
          details: t('access.detailsStall'),
          items: `Baia: ${access.stallName || 'N/A'}`,
          alert: alertLevel
        };
      
      case 'horse':
        return {
          typeKey: 'horse',
          type: t('access.typeHorse'),
          details: t('access.detailsHorse'),
          items: `Cavalo: ${access.horseName || 'N/A'}`,
          alert: alertLevel
        };
      
      default:
        return {
          typeKey: 'undefined',
          type: t('access.notDefined'),
          details: t('access.detailsUndefined'),
          items: 'N/A',
          alert: alertLevel
        };
    }
  };

  // Função para renderizar a tabela de acessos atuais
  const renderCurrentAccessTable = () => {
    if (loadingAccess) {
      return (
        <Box textAlign="center" py={2}>
          <Typography>{t('access.loadingCurrent')}</Typography>
        </Box>
      );
    }

    if (currentAccess.length === 0) {
      return (
        <Box textAlign="center" py={2}>
          <Typography variant="body2" color="textSecondary">
            {t('access.noneConfigured')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: theme.palette.action.hover }}>
              <th style={{ padding: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'left' }}>{t('fields.owner')}</th>
              <th style={{ padding: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'left' }}>{t('access.colType')}</th>
              <th style={{ padding: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'left' }}>{t('access.colDetails')}</th>
              <th style={{ padding: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'left' }}>{t('access.colItem')}</th>
              <th style={{ padding: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'left' }}>{t('access.colAlertLevel')}</th>
              <th style={{ padding: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {currentAccess.map((access, index) => {
              const accessInfo = formatAccessDescription(access);
              return (
                <tr key={index}>
                  <td style={{ padding: '12px', border: `1px solid ${theme.palette.divider}` }}>
                    {access.tenantNickName || 'N/A'}
                  </td>
                  <td style={{ padding: '12px', border: `1px solid ${theme.palette.divider}` }}>
                    <Chip 
                      label={accessInfo.type} 
                      size="small" 
                      color={
                        accessInfo.typeKey === 'full' ? 'primary' :
                        accessInfo.typeKey === 'stable' ? 'secondary' :
                        accessInfo.typeKey === 'stall' ? 'default' :
                        accessInfo.typeKey === 'horse' ? 'success' : 'error'
                      }
                    />
                  </td>
                  <td style={{ padding: '12px', border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="body2">{accessInfo.details}</Typography>
                  </td>
                  <td style={{ padding: '12px', border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="body2">{accessInfo.items}</Typography>
                  </td>
                  <td style={{ padding: '12px', border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="body2">{accessInfo.alert}</Typography>
                  </td>
                  <td style={{ padding: '12px', border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                    <Button 
                      size="small" 
                      color="error" 
                      variant="outlined"
                      onClick={() => removeAccess(access)}
                    >
                      Remover
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    );
  };

  // Função principal para salvar baseada no tipo de acesso
  const handleSave = async () => {
    // Trava de reentrada: o `disabled` do botão já impede o clique, mas um
    // Enter ou um duplo-clique rápido ainda chegariam aqui.
    if (salvando) return;

    // Verificar se tem tenant selecionado
    if (selectedTenants.length === 0) {
      alert(t("access.selectOwner"));
      return;
    }

    // Validações específicas por tipo
    switch (accessType) {
      case 'full':
        // Para acesso total, não precisa de validação adicional
        break;
      case 'stable':
        if (selectedStables.length === 0) {
          alert(t("access.selectStable"));
          return;
        }
        break;
      case 'stall':
        if (selectedStalls.length === 0) {
          alert(t("access.selectStall"));
          return;
        }
        break;
      case 'horse':
        if (selectedHorses.length === 0) {
          alert(t("access.selectHorse"));
          return;
        }
        break;
    }

    let successCount = 0;
    let errorCount = 0;
    let totalRequests = 0;

    setSalvando(true);
    try {
      // ✅ NOVO: Loop através de todos os tenants selecionados
      for (const tenantId of selectedTenants) {
        
        // CASO 1: Acesso total (uma requisição por tenant)
        if (accessType === 'full') {
          totalRequests++;
          const payload = {
            accessTenant: tenantId,
            accessAlert: accessAlert,
            userId: userId
          };

          try {
            console.log(`Enviando acesso total para tenant ${tenantId}:`, payload);
            await api.post(`/insert_access/${userId}`, payload, {
              headers: {
                'Content-Type': 'application/json',
                'X-Tenant-ID': tenantId.toString()
              }
            });
            successCount++;
          } catch (err) {
            console.error(`Erro ao salvar acesso para tenant ${tenantId}:`, err);
            errorCount++;
          }
        }
        
        // CASO 2: Acesso por baias (uma requisição para cada baia de cada tenant)
        else if (accessType === 'stall') {
          // Loop para cada baia selecionada dentro de cada tenant
          for (const stallId of selectedStalls) {
            totalRequests++;
            const payload: any = {
              accessTenant: tenantId,
              accessStall: stallId,
              userId: userId
            };

            // Adicionar alert apenas se foi modificado
            if (accessAlert !== 0) {
              payload.accessAlert = accessAlert;
            }

            try {
              console.log(`Enviando acesso para tenant ${tenantId}, baia ${stallId}:`, payload);
              await api.post(`/insert_access/${userId}`, payload, {
                headers: {
                  'Content-Type': 'application/json',
                  'X-Tenant-ID': tenantId.toString()
                }
              });
              successCount++;
            } catch (err) {
              console.error(`Erro ao salvar acesso para tenant ${tenantId}, baia ${stallId}:`, err);
              errorCount++;
            }
          }
        }
        
        // CASO 3: Acesso por estábulos
        else if (accessType === 'stable') {
          totalRequests++;
          const payload: any = {
            accessTenant: tenantId,
            accessStable: selectedStables.length === 1 
              ? selectedStables[0] 
              : selectedStables.join(','),
            userId: userId
          };

          if (accessAlert !== 0) {
            payload.accessAlert = accessAlert;
          }

          try {
            console.log(`Enviando acesso por estábulo para tenant ${tenantId}:`, payload);
            await api.post(`/insert_access/${userId}`, payload, {
              headers: {
                'Content-Type': 'application/json',
                'X-Tenant-ID': tenantId.toString()
              }
            });
            successCount++;
          } catch (err) {
            console.error(`Erro ao salvar acesso para tenant ${tenantId}:`, err);
            errorCount++;
          }
        }
        
        // CASO 4: Acesso por cavalos
        else if (accessType === 'horse') {
          totalRequests++;
          const payload: any = {
            accessTenant: tenantId,
            accessHorse: selectedHorses.length === 1 
              ? selectedHorses[0] 
              : selectedHorses.join(','),
            userId: userId
          };

          if (accessAlert !== 0) {
            payload.accessAlert = accessAlert;
          }

          try {
            console.log(`Enviando acesso por cavalo para tenant ${tenantId}:`, payload);
            await api.post(`/insert_access/${userId}`, payload, {
              headers: {
                'Content-Type': 'application/json',
                'X-Tenant-ID': tenantId.toString()
              }
            });
            successCount++;
          } catch (err) {
            console.error(`Erro ao salvar acesso para tenant ${tenantId}:`, err);
            errorCount++;
          }
        }
      }

      // Mostrar mensagem de sucesso/erro
      if (successCount > 0) {
        let message = '';
        if (totalRequests === 1) {
          message = t('access.addSuccess');
        } else {
          if (successCount === totalRequests) {
            message = `Todos os ${successCount} acessos foram adicionados com sucesso!`;
          } else {
            message = `${successCount} de ${totalRequests} acessos adicionados. ${errorCount} erro(s) ocorreram.`;
          }
        }
        
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Recarregar os acessos atuais
        await loadCurrentAccess();
        
        // Limpar formulário (apenas se não houver tenant fixo).
        // `!userTenant` apagaria a seleção do proprietário ZERO, que é fixo.
        if (userTenant === null || userTenant === undefined) {
          setSelectedTenants([]);
        }
        setSelectedStables([]);
        setSelectedStalls([]);
        setSelectedHorses([]);
        setAccessType('full');
        setAccessAlert(0);
      } else {
        throw new Error("Nenhum acesso foi salvo com sucesso");
      }

    } catch (err: any) {
      console.error("Erro geral ao salvar acessos:", err);
      alert(`Erro ao salvar acessos: ${err.message}`);
    } finally {
      setSalvando(false);
    }
  };

  const renderMultiSelect = (
    label: string,
    value: number[],
    onChange: (val: number[]) => void,
    options: any[],
    idField: string,
    labelField: string,
    openState?: boolean,
    setOpenState?: (open: boolean) => void,
    /** Escolha única: a nova seleção SUBSTITUI a anterior. */
    unico?: boolean
  ) => (
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        open={openState}
        onOpen={() => setOpenState?.(true)}
        onClose={() => setOpenState?.(false)}
        onChange={(e) => {
          const bruto = e.target.value as number[];
          if (!unico) {
            onChange(bruto);
            return;
          }
          // Fica só o que o usuário acabou de marcar.
          const novo = bruto.filter((id) => !value.includes(id));
          onChange(novo.length > 0 ? [novo[novo.length - 1]] : []);
          setOpenState?.(false);
        }}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => options
          .filter(opt => selected.includes(opt[idField]))
          .map(opt => opt[labelField])
          .join(", ")}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
          // ✅ Adicionar botão de confirmar no final da lista
          MenuListProps: {
            component: 'div',
            style: { paddingBottom: 0 }
          } as any,
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt[idField]} value={opt[idField]}>
            <Checkbox checked={value.includes(opt[idField])} />
            <ListItemText primary={opt[labelField]} />
          </MenuItem>
        ))}
        {/* ✅ Botão de confirmar fixo no final */}
        <Box 
          sx={{ 
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1
          }}
        >
          <Button 
            variant="contained" 
            color="primary" 
            size="small"
            fullWidth
            onClick={() => setOpenState?.(false)}
            sx={{ 
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            ✓ Confirmar Seleção
          </Button>
        </Box>
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t('access.title')}</DialogTitle>
      <DialogContent dividers>
        {/* Loading state */}
        {loadingUserData && (
          <Box mb={2} p={2} sx={{ backgroundColor: alpha(theme.palette.info.main, 0.12), borderRadius: 1 }}>
            <Typography variant="body2">Carregando informações do usuário...</Typography>
          </Box>
        )}

        {/* Mensagem de Sucesso */}
        {successMessage && (
          <Box mb={2} p={2} sx={{ backgroundColor: alpha(theme.palette.success.main, 0.12), color: 'success.main', borderRadius: 1, border: 1, borderColor: alpha(theme.palette.success.main, 0.3) }}>
            <Typography variant="body2">{successMessage}</Typography>
          </Box>
        )}

        {/* Seção de Acessos Atuais */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            {t('access.currentSection')}
          </Typography>
          <Box sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'action.hover',
            p: 2
          }}>
            {renderCurrentAccessTable()}
          </Box>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
          {t('access.configSection')}
        </Typography>

        {/* Aviso de tenant fixo ou seleção de proprietários */}
        <Box mb={3}>
          {userTenant ? (
            // Usuário tem tenant associado - mostrar informação fixa
            <>
              <Typography variant="h6" gutterBottom>
                {t('access.ownerFixed')}
              </Typography>
              <Box 
                sx={{
                  p: 2,
                  backgroundColor: alpha(theme.palette.info.main, 0.12),
                  border: 2,
                  borderColor: alpha(theme.palette.info.main, 0.4),
                  borderRadius: 1,
                  mb: 2
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  🏢 {userTenantName}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {t('access.tenantFixedInfo')}
                </Typography>
              </Box>
            </>
          ) : (
            // Usuário sem tenant associado - mostrar seleção normal
            <>
              <Typography variant="h6" gutterBottom>
                {t('access.ownerSelect')}
              </Typography>
              {renderMultiSelect(
                t("nav.tenants"), 
                selectedTenants, 
                setSelectedTenants, 
                tenantsDisponiveis, 
                "tenantId", 
                "tenantName",
                openTenantSelect,
                setOpenTenantSelect,
                // Acesso POR BAIA é de um proprietário só: as baias pertencem
                // a um proprietário, e cruzar vários com as baias de um
                // gravava pares que não existem no banco.
                accessType === 'stall'
              )}
              {/* ✅ Mensagem informativa quando múltiplos tenants são selecionados */}
              {selectedTenants.length > 1 && (
                <Box mt={1} p={2} sx={{ backgroundColor: alpha(theme.palette.info.main, 0.12), border: 1, borderColor: alpha(theme.palette.info.main, 0.4), borderRadius: 1 }}>
                  <Typography variant="body2" color="primary.main">
                    ℹ️ Você selecionou {selectedTenants.length} proprietários. 
                    {accessType === 'full' && ` Serão criados ${selectedTenants.length} registros de acesso (um para cada proprietário).`}

                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Tipo de acesso */}
        {selectedTenants.length > 0 && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {t('access.typeSection')}
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={accessType}
                onChange={(e) => setAccessType(e.target.value as AccessType)}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  label={t("access.optionTotal")}
                />
                <FormControlLabel
                  value="stable"
                  control={<Radio />}
                  label={t("access.optionStable")}
                />
                <FormControlLabel
                  value="stall"
                  control={<Radio />}
                  label={t("access.optionStall")}
                />
                <FormControlLabel
                  value="horse"
                  control={<Radio />}
                  label={t("access.optionHorse")}
                />
              </RadioGroup>
            </FormControl>

            {/* Informativo se existir acesso total e for selecionado acesso individualizado */}
            {accessType !== 'full' && selectedTenants.length > 0 && hasFullTenantAccess(selectedTenants[0]) && (
              <Box mt={2} p={2} sx={{ backgroundColor: alpha(theme.palette.success.main, 0.12), border: 1, borderColor: alpha(theme.palette.success.main, 0.4), borderRadius: 1 }}>
                <Typography variant="body2" color="success.main">
                  <strong>{t('access.coexistStrong')}</strong> {t('access.coexistText')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Seleções específicas baseadas no tipo */}
        {selectedTenants.length > 0 && accessType === 'stable' && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {t('access.stablesSection')}
            </Typography>
            {renderMultiSelect(
              t("nav.stables"), 
              selectedStables, 
              setSelectedStables, 
              stables, 
              "stableId", 
              "stableName",
              openStableSelect,
              setOpenStableSelect
            )}
          </Box>
        )}

        {selectedTenants.length > 0 && accessType === 'stall' && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {t('access.stallsSection')}
            </Typography>
            {renderMultiSelect(
              t("stalls.nameLabel"), 
              selectedStalls, 
              setSelectedStalls, 
              stalls, 
              "stallId", 
              "stallName",
              openStallSelect,
              setOpenStallSelect
            )}
            {selectedStalls.length > 1 && (
              <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
                {t('access.stallsMultiInfo', { count: selectedStalls.length })}
              </Typography>
            )}
          </Box>
        )}

        {selectedTenants.length > 0 && accessType === 'horse' && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {t('access.horsesSection')}
            </Typography>
            <FormGroup>
              {horses.map((horse: any) => (
                <FormControlLabel
                  key={horse.horseId}
                  control={
                    <Checkbox
                      checked={selectedHorses.includes(horse.horseId)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedHorses(prev =>
                          checked
                            ? [...prev, horse.horseId]
                            : prev.filter(id => id !== horse.horseId)
                        );
                      }}
                    />
                  }
                  label={horse.horseName}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* Nível de alerta - sempre disponível */}
        {selectedTenants.length > 0 && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {t('access.alertSection')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {accessType === 'full' 
                ? t("access.alertDescFull")
                : accessType === 'stall' && selectedStalls.length > 1
                  ? t("access.alertDescMultiStall")
                  : t("access.alertDescIndividual")
              }
            </Typography>
            <FormControl fullWidth>
              <InputLabel>{t('access.alertLevelLabel')}</InputLabel>
              <Select
                value={accessAlert}
                onChange={(e) => setAccessAlert(e.target.value as number)}
                label={t("access.alertLevelLabel")}
              >
                {alertOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={salvando}>
          {t('common.close')}
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSave}
          disabled={selectedTenants.length === 0 || loadingUserData || salvando}
          startIcon={
            salvando ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {salvando ? t('common.saving') : t('access.addAccess')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}